import type { GameContext } from "../../GameContext";

/**
 * Shared base for the six toggleable panels (CharacterSheet, SkillTree,
 * EquipmentPanel, JobAdvancementPanel, InventoryGrid, EconomyPanel).
 * WorldHud and CombatHud are always-on overlays and don't extend this.
 *
 * Every subclass talks to the server ONLY through ctx.network.rest.* --
 * none of them construct a fetch() call or hold a reference to a Worker
 * URL, per the NetworkManager chokepoint rule.
 */
export abstract class PanelBase {
  protected readonly el: HTMLDivElement;
  private visible = false;

  constructor(root: HTMLElement, protected readonly ctx: GameContext, className: string) {
    this.el = document.createElement("div");
    this.el.className = `panel ${className}`;
    this.el.style.display = "none";
    root.appendChild(this.el);
  }

  show(): void {
    this.visible = true;
    this.el.style.display = "block";
    // BUG FIX: this was a bare fire-and-forget `void this.refresh()` with
    // no .catch() — any exception thrown inside a subclass's refresh()
    // (e.g. CharacterSheet's real bug: a field-name mismatch against the
    // server response caused a synchronous TypeError mid-render) became
    // an unhandled promise rejection. The panel was left showing exactly
    // whatever text refresh() had set before the throw — "Loading..."
    // forever, with no visible error and no way for the player to tell
    // it wasn't just slow. Every panel now gets this safety net, not
    // just the one panel that happened to hit it first.
    this.refresh().catch((err) => {
      const message = err instanceof Error ? err.message : "Something went wrong loading this panel.";
      this.el.innerHTML = `<p class="panel-error">${message}</p>`;
      console.error(`[${this.el.className}] refresh() failed:`, err);
    });
  }

  hide(): void {
    this.visible = false;
    this.el.style.display = "none";
  }

  toggle(): void {
    this.visible ? this.hide() : this.show();
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** Subclasses implement their own data fetch + DOM render here. */
  protected abstract refresh(): Promise<void>;
}
